import { Link } from 'wouter';
import { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, ShieldCheck, Search, FileText, Settings2, Briefcase, Users, MapPin, LayoutDashboard, Globe, CreditCard, Settings, LogOut, CheckCircle2, TrendingUp, Plus, Clock } from 'lucide-react';
import founderPhoto from '@/assets/founder.jpg';

const ADA_WIDGET_STATEMENT_PATH =
  '/api/public/statement?token=14785828-c3a2-4791-85a4-34467b660953';

const FAQS = [
  {
    q: "Is this guaranteed lawsuit protection?",
    a: "No tool — ours included — can guarantee legal immunity or make a site \"lawsuit-proof.\" ADA Widget helps you reduce risk by improving immediate usability, monitoring common WCAG issues, and documenting your ongoing, good-faith effort. It is not a substitute for legal advice or a manual accessibility audit.",
  },
  {
    q: "What visitor controls are included?",
    a: "The widget gives visitors tools to customize how they experience your website, including text sizing, contrast and color controls, line height, dyslexia-friendly fonts, reading guides and masks, focus and link highlighting, larger cursors, reduced motion, and more.",
  },
  {
    q: "What does ADA Widget monitor?",
    a: "When you run a scan, ADA Widget checks the submitted page for common technical WCAG failures that are easily missed. This includes missing image alt-text, missing form labels, incorrect heading structures, low contrast text, and basic keyboard navigation issues.",
  },
  {
    q: "How do statements/reports help?",
    a: "A public accessibility statement and logged reports demonstrate your active, ongoing commitment to accessibility. By building a historical record of your improvements and providing clear contact information, you show that you take usability seriously and are actively working on it.",
  },
  {
    q: "Is it easy to install / platform support?",
    a: "Yes. If you can add a custom <script> tag to your site's header, ADA Widget will work. We provide step-by-step, copy-paste installation guidance for WordPress, Shopify, Wix, Squarespace, Webflow, and custom websites.",
  },
  {
    q: "Is it legal advice?",
    a: "No. ADA Widget is a software tool designed to help you identify technical WCAG issues and provide practical usability controls for your visitors. We are not attorneys, and using our tool does not constitute legal advice.",
  },
];

const TIERS = [
  {
    name: "Starter",
    price: 15,
    sites: "1 website",
    features: ["Accessibility visitor controls", "Common issue monitoring", "Accessibility statement generator", "Step-by-step setup guidance"],
    ctaLabel: "Get started",
    href: "/sign-up"
  },
  {
    name: "Agency",
    price: "Custom",
    sites: "Multiple client websites",
    audience: "For agencies managing a growing client roster.",
    ctaLabel: "Talk to us",
    features: ["Everything in Starter", "Multiple websites in one account", "Dashboard overview across sites", "Shareable remediation reports"],
    isAgency: true
  }
];

type MarketingEventName =
  | 'hero_cta_click'
  | 'how_it_works_cta_click'
  | 'pricing_cta_click'
  | 'faq_opened'
  | 'final_cta_click'
  | 'signup_link_clicked';

type MarketingEventProperties = Record<string, string | number | boolean>;

function trackMarketingEvent(
  eventName: MarketingEventName,
  properties: MarketingEventProperties = {},
) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent('ada-widget:analytics', {
      detail: { eventName, properties },
    }),
  );

  // TODO: Forward this event name and properties to the selected analytics provider.
}

function trackSignupLinkClick(
  location: string,
  source: string,
  properties: MarketingEventProperties = {},
) {
  trackMarketingEvent('signup_link_clicked', { location, source, ...properties });
}

export default function HomePage() {
  const [agencyFormOpen, setAgencyFormOpen] = useState(false);

  const handleFaqValueChange = (value: string) => {
    if (!value) return;

    const question = FAQS[Number(value.replace('item-', ''))]?.q;
    if (question) {
      trackMarketingEvent('faq_opened', { question });
    }
  };

  return (
    <div className="landing-page min-h-[100dvh] bg-[#102645] text-slate-50 font-sans selection:bg-[#5687FF] selection:text-slate-950 flex flex-col font-normal">
      <style>{`
        .landing-page :is(a, button):focus-visible {
          outline: 3px solid #22C55E;
          outline-offset: 3px;
        }
      `}</style>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-md focus:bg-[#22C55E] focus:px-4 focus:py-2 focus:text-slate-950 focus:shadow-md focus:outline-none focus:font-bold"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#102645]/95 backdrop-blur-md border-b border-[#21477D]/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-bold text-lg md:text-xl tracking-tight text-white" aria-label="ADA Widget home">
            ADA Widget
          </Link>
          
          <nav aria-label="Primary" className="hidden md:flex items-center gap-8 text-[15px] font-semibold text-slate-300">
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </nav>

          <div className="flex items-center gap-4 sm:gap-5">
            <Link href="/sign-in" data-testid="link-signin" className="text-[15px] font-semibold text-slate-300 hover:text-white transition-colors hidden sm:inline-block">
              Log in
            </Link>
            <Link
              href="/sign-up"
              data-testid="link-signup"
              onClick={() => trackSignupLinkClick('header', 'header_sign_up')}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-5 h-10 bg-[#22C55E] text-slate-950 hover:bg-[#16A34A] transition-colors text-[15px] font-bold shadow-sm"
            >
              Sign up
            </Link>
          </div>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        
        {/* Hero Section */}
        <section className="pt-14 pb-16 md:pt-24 md:pb-24 px-6 overflow-hidden relative">
          <div className="mx-auto max-w-7xl grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center relative z-10">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#21477D]/60 border border-[#3062A5] mb-5 text-[12px] font-bold uppercase tracking-[0.08em] text-slate-300">
                <span className="w-2 h-2 rounded-full bg-[#5687FF]" aria-hidden="true"></span>
                Built for Small Business by Small Business
              </div>
              <h1 className="text-[2.75rem] sm:text-6xl md:text-[72px] font-extrabold leading-[1.08] tracking-tight text-white">
                We got burned,<br className="hidden md:block"/>
                <span className="text-[#5687FF]"> so you don't have to.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-[#8A9BB3] leading-relaxed max-w-xl">
                Built by business owners who faced an accessibility claim. ADA Widget helps you improve visitor usability, monitor common WCAG gaps, and document your ongoing good-faith effort.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link
                  href="/sign-up"
                  data-testid="button-cta-signup"
                  onClick={() => {
                    trackMarketingEvent('hero_cta_click', { source: 'hero_primary_cta' });
                    trackSignupLinkClick('hero', 'hero_primary_cta');
                  }}
                   className="inline-flex items-center justify-center h-14 px-8 rounded-lg bg-[#22C55E] text-slate-950 hover:bg-[#16A34A] transition-colors text-[16px] font-bold w-full sm:w-auto shadow-sm"
                >
                  Start your accessibility plan
                </Link>
                <a
                  href="#how-it-works"
                  onClick={() => trackMarketingEvent('how_it_works_cta_click', { source: 'hero_secondary_cta' })}
                   className="inline-flex items-center justify-center h-14 px-8 rounded-lg border border-[#3062A5] bg-transparent text-white hover:bg-[#21477D] transition-colors text-[16px] font-bold w-full sm:w-auto"
                >
                  See how it works
                </a>
              </div>
              
              <div className="mt-5 flex flex-wrap items-center gap-3 text-[14px] text-slate-400 font-medium">
                <span>Simple 5-minute install</span>
                <span className="w-1 h-1 rounded-full bg-slate-600" aria-hidden="true"></span>
                <span>Starts at $15/mo</span>
                <span className="w-1 h-1 rounded-full bg-slate-600" aria-hidden="true"></span>
                <span>No complicated setup</span>
              </div>
            </div>

            <div
              role="img"
              aria-label="Illustration of ADA Widget's dashboard showing Total Sites, Verified Sites, Monthly Visits, Average Score, and navigation."
               className="relative z-10 w-full rounded-2xl border border-[#3062A5]/80 bg-[#16335C] shadow-xl overflow-hidden mt-2 lg:mt-0 flex min-h-[400px]"
            >
              {/* Compact Sidebar */}
              <div className="w-48 bg-[#0B1B33] border-r border-[#21477D] p-3 flex flex-col hidden sm:flex">
                 <div className="font-bold text-white mb-6 px-2 flex items-center gap-2">
                   <div className="w-5 h-5 rounded bg-[#3062A5] flex items-center justify-center">
                     <Settings2 className="w-3 h-3 text-white" />
                   </div>
                   Workspace
                 </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#21477D] text-slate-100 font-medium text-[13px]">
                    <LayoutDashboard className="w-4 h-4 text-[#5687FF]" /> Dashboard
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                    <Globe className="w-4 h-4" /> Sites
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                    <CreditCard className="w-4 h-4" /> Billing
                  </div>
                  <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                    <Settings className="w-4 h-4" /> Settings
                  </div>
                </div>
                <div className="mt-auto border-t border-[#21477D] pt-3">
                  <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
               <div className="flex-1 p-4 sm:p-6 bg-[#16335C] flex flex-col gap-5">
                 <div className="flex flex-wrap items-start justify-between gap-3">
                   <div>
                     <h3 className="text-xl font-bold text-white tracking-tight mb-1">Dashboard</h3>
                     <p className="text-sm text-slate-400">Overview of your accessibility monitoring</p>
                   </div>
                   <span className="rounded-full border border-[#3062A5] bg-[#0B1B33] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8A9BB3]">
                     Built for Small Business by Small Business
                   </span>
                 </div>

                 {/* Stats Grid */}
                 <div className="grid grid-cols-2 gap-4">
                   <div className="p-4 rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] flex flex-col justify-between">
                     <div className="w-8 h-8 rounded bg-[#5687FF]/10 flex items-center justify-center mb-3">
                       <Globe className="w-4 h-4 text-[#5687FF]" />
                     </div>
                     <div className="text-2xl font-bold text-white mb-0.5">3</div>
                     <div className="text-[12px] text-slate-400">Total Sites</div>
                   </div>
                   <div className="p-4 rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] flex flex-col justify-between">
                     <div className="w-8 h-8 rounded bg-[#5687FF]/10 flex items-center justify-center mb-3">
                       <CheckCircle2 className="w-4 h-4 text-[#5687FF]" />
                     </div>
                     <div className="text-2xl font-bold text-white mb-0.5">3</div>
                     <div className="text-[12px] text-slate-400">Verified Sites</div>
                   </div>
                   <div className="p-4 rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] flex flex-col justify-between">
                     <div className="w-8 h-8 rounded bg-[#21477D] flex items-center justify-center mb-3">
                       <TrendingUp className="w-4 h-4 text-slate-300" />
                     </div>
                     <div className="text-2xl font-bold text-white mb-0.5">14,250</div>
                     <div className="text-[12px] text-slate-400">Monthly Visits</div>
                   </div>
                   <div className="p-4 rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] flex flex-col justify-between">
                     <div className="w-8 h-8 rounded bg-[#3062A5]/30 flex items-center justify-center mb-3">
                       <Search className="w-4 h-4 text-slate-300" />
                     </div>
                     <div className="text-2xl font-bold text-white mb-0.5">78/100</div>
                     <div className="text-[12px] text-slate-400">Average Score</div>
                   </div>
                 </div>

                 {/* Recent Activity */}
                 <div className="rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] p-4 flex-1">
                   <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                     Recent Activity
                   </h4>
                   <div className="space-y-3">
                     <div className="flex items-start gap-3">
                       <div className="w-2 h-2 rounded-full bg-[#5687FF] mt-1.5 shrink-0" />
                       <div>
                         <p className="text-[13px] font-medium text-white mb-0.5">Installation verified</p>
                         <p className="text-[11px] text-slate-400">acme-corp.com — 2 hours ago</p>
                       </div>
                     </div>
                     <div className="flex items-start gap-3">
                       <div className="w-2 h-2 rounded-full bg-[#5687FF] mt-1.5 shrink-0" />
                       <div>
                         <p className="text-[13px] font-medium text-white mb-0.5">Scan completed</p>
                         <p className="text-[11px] text-slate-400">acme-corp.com — 1 day ago</p>
                       </div>
                     </div>
                   </div>
                 </div>
              </div>
            </div>
          </div>
        </section>

        {/* Risk / Stats Section */}
         <section className="bg-[#0B1B33] py-20 md:py-24 px-6 border-y border-[#21477D]">
          <div className="mx-auto max-w-7xl">
             <div className="max-w-3xl mb-10 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4">
                The gaps you don't see are the ones that matter.
              </h2>
              <p className="text-[#8A9BB3] text-lg font-medium leading-relaxed">
                Accessibility risk is real. Claims often target small businesses lacking fundamental accessibility compliance. Being prepared is practical.
              </p>
            </div>

             <div className="grid md:grid-cols-3 gap-6">
              {[
                { stat: "5,000+", label: "ADA website lawsuits filed in 2025" },
                { stat: "~69%", label: "Targeted businesses under $25M revenue" },
                { stat: "$5k–$25k", label: "Common settlement range for demand letters" },
              ].map((s) => (
                 <div key={s.label} className="p-6 md:p-8 rounded-xl bg-[#102645] border border-[#21477D] flex flex-col justify-center">
                  <div className="text-4xl md:text-5xl font-extrabold text-[#5687FF] mb-4">{s.stat}</div>
                  <p className="text-[15px] text-slate-300 font-medium leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who this is for */}
         <section className="py-20 md:py-24 px-6 bg-[#102645]">
          <div className="mx-auto max-w-7xl">
             <div className="mb-10 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight text-center">
                Who this is for
              </h2>
            </div>

             <div className="grid md:grid-cols-3 gap-6">
               <div className="p-6 md:p-8 rounded-xl bg-[#16335C] border border-[#21477D] flex flex-col hover:border-[#3062A5] transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#5687FF]/10 flex items-center justify-center border border-[#5687FF]/20 mb-6">
                  <Briefcase className="w-6 h-6 text-[#5687FF]" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Small business owners</h3>
                <p className="text-[15px] text-slate-300 leading-relaxed font-medium">
                  A practical path for owners who want visitor tools, a clear view of common gaps, and a record of ongoing effort without becoming accessibility experts.
                </p>
              </div>

               <div className="p-6 md:p-8 rounded-xl bg-[#16335C] border border-[#21477D] flex flex-col hover:border-[#3062A5] transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#22C55E]/10 flex items-center justify-center border border-[#22C55E]/20 mb-6">
                  <Users className="w-6 h-6 text-[#22C55E]" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Agencies managing client websites</h3>
                <p className="text-[15px] text-slate-300 leading-relaxed font-medium">
                  A repeatable way to monitor client sites, give clients accessible visitor controls, and share a clearer accessibility record.
                </p>
              </div>

               <div className="p-6 md:p-8 rounded-xl bg-[#16335C] border border-[#21477D] flex flex-col hover:border-[#3062A5] transition-colors">
                <div className="w-12 h-12 rounded-lg bg-[#5687FF]/10 flex items-center justify-center border border-[#5687FF]/20 mb-6">
                  <MapPin className="w-6 h-6 text-[#5687FF]" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Multi-location or growing brands</h3>
                <p className="text-[15px] text-slate-300 leading-relaxed font-medium">
                  One place to stay organized as the number of pages, locations, teams, or customer touchpoints grows.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What ADA Widget Does (Product Proof / Dashboard) */}
        <section id="how-it-works" className="scroll-mt-24 py-20 md:py-28 px-6 bg-[#102645] overflow-hidden">
          <div className="mx-auto max-w-7xl flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="flex-1 space-y-6 pt-0 lg:pt-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5687FF]/10 border border-[#5687FF]/20 text-[12px] font-bold uppercase tracking-[0.08em] text-[#7D98FF]">
                How it works
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
                Make accessibility easier to understand and act on.
              </h2>
              <p className="text-[#8A9BB3] text-lg leading-relaxed">
                 Give visitors practical usability controls while building a clearer record of your accessibility work through repeat scans, readable reports, and transparent documentation.
              </p>

              <div className="space-y-6 pt-2">
                {[
                  { title: "Add visitor controls", desc: "Install the widget with a copy-paste snippet to offer text sizing, contrast, layout controls, and more.", icon: <Settings2 className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> },
                  { title: "Review common issues", desc: "Run automated scans for foundational WCAG gaps and get clear guidance on where you can improve.", icon: <Search className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> },
                  { title: "Build an ongoing record", desc: "Publish an accessibility statement and save reports that document your ongoing, good-faith effort.", icon: <FileText className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> }
                ].map((f, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#22C55E]/10 flex items-center justify-center shrink-0 border border-[#22C55E]/20 mt-1">
                      {f.icon}
                    </div>
                    <div>
                      <h3 className="text-[17px] font-bold text-white mb-1">{f.title}</h3>
                      <p className="text-[15px] text-slate-400 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Accurate Sites screen visual */}
            <div className="flex-[1.2] w-full relative">
              <div
                role="img"
                aria-label="Illustration of the ADA Widget Sites screen showing real navigation, website installation status, accessibility score, and monthly visits."
                 className="relative border border-[#3062A5]/80 bg-[#16335C] rounded-2xl shadow-xl flex flex-col md:flex-row overflow-hidden min-h-[420px] md:min-h-[500px]"
              >
                <div className="w-full md:w-52 bg-[#0B1B33] border-b md:border-b-0 md:border-r border-[#21477D] p-3 md:p-4 flex flex-col">
                  <div className="font-bold text-white mb-4 md:mb-8 px-2">ADA Widget</div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </div>
                    <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#21477D] text-slate-100 font-medium text-[13px]">
                      <Globe className="w-4 h-4 text-[#5687FF]" /> Sites
                    </div>
                    <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                      <CreditCard className="w-4 h-4" /> Billing
                    </div>
                    <div className="flex items-center gap-3 px-3 py-2 rounded text-slate-400 font-medium text-[13px]">
                      <Settings className="w-4 h-4" /> Settings
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-4 md:p-6 flex flex-col gap-5">
                  <div className="flex flex-wrap justify-between items-start gap-3">
                    <div>
                      <h3 className="font-bold text-xl text-white">Sites</h3>
                      <p className="mt-1 text-[12px] text-slate-400">Manage your websites and accessibility widgets</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-[#22C55E] px-3 py-2 text-[11px] font-bold text-slate-950">
                      <Plus className="h-3.5 w-3.5" /> Add Site
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { name: 'Main Website', domain: 'yourbusiness.com', status: 'Verified', score: '84', visits: '1,248', verified: true },
                      { name: 'Second Location', domain: 'location.yourbusiness.com', status: 'Pending', score: '—', visits: '0', verified: false },
                    ].map((site) => (
                      <div key={site.domain} className="rounded-lg border border-[#3062A5]/50 bg-[#1E3E6E] p-4">
                        <div className="mb-5 flex items-start justify-between gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#5687FF]/10">
                            <Globe className="h-5 w-5 text-[#5687FF]" />
                          </div>
                          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-bold ${site.verified ? 'bg-[#22C55E]/10 text-[#4ADE80]' : 'bg-[#21477D] text-slate-300'}`}>
                            {site.verified ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                            {site.status}
                          </span>
                        </div>
                        <p className="font-bold text-white">{site.name}</p>
                        <p className="mt-1 text-[11px] text-slate-400">{site.domain}</p>
                        <div className="mt-5 flex justify-between border-t border-[#3062A5]/40 pt-3 text-[11px]">
                          <span className="text-slate-400">Score: <strong className="text-white">{site.score}</strong></span>
                          <span className="text-slate-400">Visits: <strong className="text-white">{site.visits}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-auto text-[11px] text-slate-500">Illustrative data shown in the current ADA Widget dashboard layout.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What happens after signup */}
        <section className="py-20 md:py-24 border-t border-[#21477D] px-6 bg-[#102645]">
          <div className="mx-auto max-w-7xl">
             <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-10 md:mb-12 text-center">
              What happens after signup
            </h2>
             <div className="grid md:grid-cols-4 gap-8 md:gap-8 relative">
              {/* Desktop connecting line */}
              <div className="hidden md:block absolute top-6 left-[12%] right-[12%] h-[2px] bg-[#21477D] z-0"></div>

              {[
                { step: "01", title: "Create your account", desc: "Sign up in seconds and add your website details to begin the process." },
                { step: "02", title: "Install the widget", desc: "Add a simple script tag. Step-by-step guidance is included for WordPress, Shopify, Wix, Squarespace, Webflow, and custom websites." },
                 { step: "03", title: "Prioritize improvements", desc: "Use clear findings to prioritize the accessibility gaps that matter most." },
                { step: "04", title: "Publish your statement", desc: "Generate your accessibility statement and track ongoing progress." },
              ].map((s, i) => (
                <div key={i} className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#16335C] border-2 border-[#3062A5] flex items-center justify-center text-[#7D98FF] font-bold mb-6 text-[15px] shadow-sm">
                    {s.step}
                  </div>
                  <h3 className="text-[17px] font-bold text-white mb-3">{s.title}</h3>
                  <p className="text-[14px] text-slate-400 font-medium leading-relaxed max-w-[220px]">
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Story */}
        <section className="py-16 md:py-20 px-6 bg-[#0B1B33] border-y border-[#21477D]">
          <div className="mx-auto max-w-5xl">
             <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center p-6 md:p-10 rounded-xl bg-[#102645] border border-[#21477D]">
              <div className="shrink-0 relative">
                <img
                  src={founderPhoto}
                  alt="Portrait of the ADA Widget founder"
                  width={192}
                  height={192}
                  loading="lazy"
                  className="relative w-32 h-32 md:w-48 md:h-48 rounded-2xl object-cover shadow-lg border border-[#3062A5]"
                />
              </div>
              <div className="flex-1">
                 <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight leading-snug mb-5">
                  "I built ADA Widget because I wanted the solution I wish I’d had."
                </h2>
                <div className="space-y-4 text-[16px] text-slate-300 leading-relaxed font-medium">
                  <p>
                    A few years ago, my small business was sued over website accessibility and WCAG issues. The result was stress, attorney fees, confusion, and ultimately a settlement that cost me thousands. It opened my eyes to how vulnerable small businesses can be when they don’t have the right accessibility tools in place.
                  </p>
                  <p>
                    That experience led me to build ADA Widget—a simple way to improve accessibility for visitors, identify potential issues, and take proactive steps toward a more accessible website.
                  </p>
                  <p className="font-bold text-white pt-2 text-sm uppercase tracking-wider">— The ADA Widget team</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
         <section id="pricing" aria-labelledby="pricing-heading" className="scroll-mt-24 py-20 md:py-28 px-6 bg-[#102645]">
          <div className="mx-auto max-w-7xl">
             <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10 md:mb-12">
              <div className="max-w-2xl">
                <h2 id="pricing-heading" className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                  Affordable tools built for real small businesses.
                </h2>
                <p className="mt-6 text-lg text-slate-400 font-medium">
                   No enterprise pricing or technical expertise required. Clear ongoing monitoring and documentation.
                </p>
              </div>
            </div>

            {/* Support Strip */}
             <div id="support" className="scroll-mt-24 flex flex-wrap items-center justify-center gap-6 md:gap-16 py-5 mb-6 border-y border-[#21477D] bg-[#16335C]/30 rounded-xl">
               <div className="flex items-center gap-2.5 text-white font-bold text-[15px]">
                 <Check className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> Email support
               </div>
                 <div className="flex items-center gap-2.5 text-white font-bold text-[15px]">
                   <Check className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> Installation help
               </div>
               <div className="flex items-center gap-2.5 text-white font-bold text-[15px]">
                 <Check className="w-5 h-5 text-[#22C55E]" aria-hidden="true" /> Straightforward onboarding
               </div>
            </div>

             <div id="legal-clarity" className="scroll-mt-24 mb-8 md:mb-10 rounded-xl border border-[#3062A5]/70 bg-[#16335C] px-5 py-4 md:px-6 md:py-5">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:gap-4">
                <ShieldCheck className="h-5 w-5 shrink-0 text-[#5687FF] md:mt-0.5" aria-hidden="true" />
                <p className="text-[14px] leading-relaxed text-slate-300">
                  ADA Widget supports your accessibility efforts with visitor tools, monitoring, and documentation. No accessibility solution can guarantee full compliance or prevent claims or lawsuits. Results may vary based on your website, content, and ongoing accessibility practices.
                </p>
              </div>
            </div>

             <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {TIERS.map((tier) => {
                return (
                  <div
                    key={tier.name}
                    className={`flex flex-col rounded-xl bg-[#16335C] border ${
                      !tier.isAgency
                        ? "border-[#5687FF] shadow-lg ring-1 ring-[#5687FF]/30 relative transform lg:-translate-y-2" 
                        : "border-[#21477D] shadow-sm hover:border-slate-600 transition-colors"
                     } p-6 md:p-8`}
                  >
                    {!tier.isAgency && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#5687FF] text-slate-950 text-[11px] font-extrabold uppercase tracking-wide px-3 py-1 rounded-full shadow-md whitespace-nowrap">
                        Launch Plan
                      </div>
                    )}
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-white tracking-tight">{tier.name}</h3>
                      <div className="mt-4 flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-white">{typeof tier.price === 'number' ? `$${tier.price}` : tier.price}</span>
                        {typeof tier.price === 'number' && <span className="text-slate-400 font-bold text-sm">/mo</span>}
                      </div>
                      <div className="h-5 mt-1">
                        {typeof tier.price === 'number' && (
                          <p className="text-[13px] text-slate-400 font-medium">Billed monthly</p>
                        )}
                      </div>
                      <p className="mt-6 text-[14px] font-medium text-slate-300 pb-5 border-b border-[#21477D]">
                        {tier.sites}
                      </p>
                      {tier.audience && (
                        <p className="mt-3 text-[13px] leading-relaxed text-slate-400">
                          {tier.audience}
                        </p>
                      )}
                    </div>
                    
                    <ul className="space-y-4 flex-1 mb-8">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-[14px] text-slate-300 font-medium">
                          <Check className="h-5 w-5 text-[#22C55E] shrink-0" aria-hidden="true" />
                          <span className="leading-snug">{f}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {tier.isAgency ? (
                      <button
                        type="button"
                        aria-label={`${tier.ctaLabel ?? 'Get started'} with the ${tier.name} plan`}
                        data-testid={`button-pricing-${tier.name.toLowerCase()}`}
                        onClick={() => {
                          trackMarketingEvent('pricing_cta_click', {
                            plan_name: tier.name,
                          });
                          setAgencyFormOpen(true);
                        }}
                        className="w-full h-12 flex items-center justify-center rounded-md text-[15px] font-bold transition-all bg-[#21477D] text-white hover:bg-[#3062A5]"
                      >
                        {tier.ctaLabel}
                      </button>
                    ) : (
                      <Link
                        href={tier.href || "/sign-up"}
                        aria-label={`${tier.ctaLabel ?? 'Get started'} with the ${tier.name} plan`}
                        data-testid={`link-pricing-${tier.name.toLowerCase()}`}
                        onClick={() => {
                          trackMarketingEvent('pricing_cta_click', {
                            plan_name: tier.name,
                          });
                          trackSignupLinkClick('pricing', 'pricing_plan_card', {
                            plan_name: tier.name,
                          });
                        }}
                        className="w-full h-12 flex items-center justify-center rounded-md text-[15px] font-bold transition-all bg-[#22C55E] text-slate-950 hover:bg-[#16A34A] shadow-sm"
                      >
                        {tier.ctaLabel ?? 'Get started'}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
         <section id="faq" aria-labelledby="faq-heading" className="scroll-mt-24 py-20 md:py-28 px-6 bg-[#0B1B33] border-y border-[#21477D]">
          <div className="mx-auto max-w-3xl">
             <h2 id="faq-heading" className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-8 md:mb-12">
              Questions & Answers
            </h2>
            <Accordion
              type="single"
              collapsible
              aria-label="Frequently asked questions"
              onValueChange={handleFaqValueChange}
              className="w-full"
            >
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={"item-" + i} className="border-b-slate-800">
                   <AccordionTrigger className="text-[17px] font-bold text-white hover:no-underline hover:text-[#5687FF] py-5 md:py-6 text-left transition-colors">
                    {f.q}
                  </AccordionTrigger>
                   <AccordionContent className="text-[15px] text-slate-400 font-medium leading-relaxed pb-5 md:pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
         <section aria-labelledby="closing-cta-heading" className="py-24 md:py-28 px-6 bg-[#102645] text-center relative overflow-hidden">
          <div className="relative z-10 mx-auto max-w-3xl">
            <h2 id="closing-cta-heading" className="text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Support your business. <br className="hidden sm:block" />
              <span className="text-[#5687FF]">Welcome more customers.</span>
            </h2>
             <p className="mt-6 text-lg md:text-xl text-slate-400 max-w-2xl mx-auto font-medium">
              Start improving your website today by identifying fundamental gaps and adding essential visitor tools.
            </p>
             <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                href="/sign-up"
                data-testid="button-cta-bottom"
                onClick={() => {
                  trackMarketingEvent('final_cta_click', { source: 'final_cta' });
                  trackSignupLinkClick('final_cta', 'final_cta');
                }}
                className="inline-flex items-center justify-center h-14 px-10 rounded-lg bg-[#22C55E] text-slate-950 font-bold text-[16px] hover:bg-[#16A34A] transition-colors shadow-sm"
              >
                Get started today
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B1B33] py-12 px-6 border-t border-[#21477D]">
        <div className="mx-auto max-w-7xl flex flex-col md:flex-row items-center justify-between gap-6 text-[14px] text-slate-400 font-medium">
          <div className="font-bold text-xl text-white">
            ADA Widget
          </div>
          <div className="flex flex-col items-center gap-3 text-center md:items-start md:text-left">
            <span>© {new Date().getFullYear()} ADA Widget. All rights reserved.</span>
            <nav aria-label="Legal and support" className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-slate-400 md:justify-start">
              <Link href="/terms" data-testid="link-footer-terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link href="/privacy" data-testid="link-footer-privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link href="/refund" data-testid="link-footer-refund" className="hover:text-white transition-colors">Refund / Cancellation Policy</Link>
              <a
                href={ADA_WIDGET_STATEMENT_PATH}
                className="hover:text-white transition-colors"
                data-testid="link-accessibility-statement"
              >
                Accessibility Statement
              </a>
              <span aria-disabled="true" data-testid="status-footer-support" title="A branded support email is being set up">Contact / Support coming soon</span>
            </nav>
          </div>
          <div className="text-center md:text-right max-w-md leading-relaxed">
            ADA Widget helps businesses improve website accessibility and work toward WCAG best practices. No accessibility solution can guarantee full compliance or prevent claims or lawsuits. Results may vary based on your website, content, and ongoing accessibility practices.
          </div>
        </div>
      </footer>

      {/* Agency Interest Dialog */}
      <Dialog open={agencyFormOpen} onOpenChange={setAgencyFormOpen}>
        <DialogContent className="bg-[#16335C] border-[#3062A5] text-white sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Partner with us</DialogTitle>
            <DialogDescription className="text-slate-300">
              We are setting up a branded contact email before accepting sales inquiries. This form shows what we will ask for, but it cannot submit yet.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4 mt-4" onSubmit={(e) => e.preventDefault()} aria-describedby="agency-form-status">
            <p id="agency-form-status" data-testid="status-agency-form" className="rounded-md border border-[#5687FF]/30 bg-[#0B1B33] px-3 py-2 text-sm text-slate-300">
              Contact form coming soon. No information entered here will be submitted.
            </p>
            <div className="space-y-2">
              <label htmlFor="agency-name" className="text-sm font-semibold">Agency Name</label>
              <input id="agency-name" type="text" data-testid="input-agency-name" disabled className="w-full bg-[#0B1B33] border border-[#3062A5] rounded-md h-10 px-3 opacity-50 cursor-not-allowed text-white" placeholder="Acme Digital" />
            </div>
            <div className="space-y-2">
              <label htmlFor="agency-email" className="text-sm font-semibold">Work Email</label>
              <input id="agency-email" type="email" data-testid="input-agency-email" disabled className="w-full bg-[#0B1B33] border border-[#3062A5] rounded-md h-10 px-3 opacity-50 cursor-not-allowed text-white" placeholder="hello@acme.com" />
            </div>
            <button type="submit" data-testid="button-agency-submit" disabled className="w-full h-10 rounded-md bg-[#21477D] text-white font-bold opacity-50 cursor-not-allowed mt-2">
              Contact form coming soon
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
